import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

const apiPrefix = process.env.REACT_APP_API_PREFIX;

const AdminConnectionHistory = () => {
    const token = useSelector((state) => state.user.token);
    const appNumber = useSelector((state) => state.app.appNumber);

    const [data, setData] = useState([]);
    const [summary, setSummary] = useState({ totalConnections: 0, monthsWithData: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchConnectionHistory = async () => {
            try {
                const response = await fetch(`${apiPrefix}admin/connection-history`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'X-App-Number': String(appNumber),
                    },
                });

                if (!response.ok) {
                    throw new Error('Erreur lors de la récupération de l\'historique');
                }

                const result = await response.json();
                setData(result.data || []);
                setSummary(result.summary || { totalConnections: 0, monthsWithData: 0 });
            } catch (err) {
                console.error('Erreur fetchConnectionHistory:', err);
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchConnectionHistory();
    }, [apiPrefix, token, appNumber]);

    const maxConnections = data.length > 0
        ? Math.max(...data.map((d) => d.totalConnections), 1)
        : 1;

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 py-8 px-4">
                <div className="max-w-6xl mx-auto flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-primary"></div>
                    <span className="ml-3 text-gray-600">Chargement de l'historique...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 py-8 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        {error}
                    </div>
                    <Link to="/dashboard" className="inline-block mt-4 text-theme-primary hover:underline">
                        <i className="fas fa-arrow-left mr-2"></i>Retour au tableau de bord
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4">
            <div className="max-w-6xl mx-auto">
                <div className="mb-8 flex items-center justify-between">
                    <div>
                        <Link to="/dashboard" className="text-theme-primary hover:underline mb-2 inline-block">
                            <i className="fas fa-arrow-left mr-2"></i>Tableau de bord
                        </Link>
                        <h1 className="text-4xl font-bold text-theme-primary">
                            Historique des connexions (48 mois)
                        </h1>
                        <p className="text-gray-600 mt-2">
                            KPIs direction – Connexions par mois
                        </p>
                    </div>
                </div>

                {summary.totalConnections === 0 && summary.monthsWithData === 0 ? (
                    <div className="card-theme p-8 text-center">
                        <i className="fas fa-chart-line text-5xl text-gray-300 mb-4"></i>
                        <p className="text-gray-600 text-lg">
                            Aucune donnée de connexion enregistrée pour le moment.
                        </p>
                        <p className="text-gray-500 mt-2">
                            Les statistiques seront disponibles après les premières connexions des utilisateurs.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            <div className="card-theme p-6">
                                <div className="flex items-center">
                                    <div className="bg-theme-primary text-white p-3 rounded-lg mr-4">
                                        <i className="fas fa-sign-in-alt text-xl"></i>
                                    </div>
                                    <div>
                                        <p className="text-sm text-theme-primary font-medium">Total des connexions</p>
                                        <p className="text-2xl font-bold text-theme-primary-active">
                                            {summary.totalConnections.toLocaleString('fr-FR')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="card-theme p-6">
                                <div className="flex items-center">
                                    <div className="bg-theme-accent text-white p-3 rounded-lg mr-4">
                                        <i className="fas fa-calendar-alt text-xl"></i>
                                    </div>
                                    <div>
                                        <p className="text-sm text-theme-primary font-medium">Mois avec données</p>
                                        <p className="text-2xl font-bold text-theme-primary-active">
                                            {summary.monthsWithData}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card-theme overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Mois
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Connexions totales
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Utilisateurs uniques
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                                                Répartition
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {data.map((row) => (
                                            <tr key={`${row.year}-${row.month}`} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {row.label}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                    {row.totalConnections.toLocaleString('fr-FR')}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                    {row.uniqueUsers.toLocaleString('fr-FR')}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="w-full bg-gray-200 rounded-full h-4">
                                                        <div
                                                            className="bg-theme-primary h-4 rounded-full transition-all"
                                                            style={{
                                                                width: `${Math.min(100, (row.totalConnections / maxConnections) * 100)}%`,
                                                                minWidth: row.totalConnections > 0 ? '4px' : '0',
                                                            }}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AdminConnectionHistory;
